import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../Api/api";

// ─── Icons ──────────────────────────────────────────────────────────────
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconTime = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7B8DFF" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7B8DFF" strokeWidth="2" strokeLinecap="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconCamera = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const IconFlip = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/>
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
  </svg>
);
const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconRetake = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="1 4 1 10 7 10"/>
    <path d="M3.51 15a9 9 0 1 0 .49-4.95"/>
  </svg>
);

export default function CheckIn() {
  const navigate = useNavigate();
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [photo,      setPhoto]      = useState(null);   // base64 data URL
  const [photoBlob,  setPhotoBlob]  = useState(null);   // Blob for upload
  const [location,   setLocation]   = useState(null);
  const [time,       setTime]       = useState("");
  const [loading,    setLoading]    = useState(false);
  const [facingFront, setFacingFront] = useState(true);
  const [camReady,   setCamReady]   = useState(false);
  const [camError,   setCamError]   = useState("");
  const [locError,   setLocError]   = useState("");
  const [flash,      setFlash]      = useState(false);  // shutter flash

  // ── Live clock ──────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // ── Location ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) { setLocError("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => setLocError("Location access denied"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // ── Camera ──────────────────────────────────────────────────────────
  const startCamera = useCallback(async (front = true) => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setCamReady(false);
    setCamError("");
    try {
      const constraints = {
        video: {
          facingMode: front ? "user" : "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCamReady(true);
      }
    } catch {
      setCamError("Camera access denied. Please allow camera in browser settings.");
    }
  }, []);

  useEffect(() => {
    startCamera(facingFront);
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, [facingFront]);

  // ── Take photo ──────────────────────────────────────────────────────
  const takePhoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");

    // Mirror if front camera
    if (facingFront) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);

    // Shutter flash
    setFlash(true);
    setTimeout(() => setFlash(false), 200);

    canvas.toBlob((blob) => {
      setPhotoBlob(blob);
      setPhoto(canvas.toDataURL("image/jpeg", 0.85));
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    }, "image/jpeg", 0.85);
  };

  // ── Retake ──────────────────────────────────────────────────────────
  const retake = () => {
    setPhoto(null);
    setPhotoBlob(null);
    startCamera(facingFront);
  };

  // ── Confirm check-in ────────────────────────────────────────────────
  const confirmCheckIn = async () => {
    if (!photo)    return alert("Please take a photo first");
    if (!location) return alert("Waiting for location — please try again");
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("photo",    new File([photoBlob], "checkin.jpg", { type: "image/jpeg" }));
      formData.append("location", JSON.stringify({ latitude: location.latitude, longitude: location.longitude }));
      const res = await API.post("/attendance/checkin", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        navigate("/dashboard", { replace: true });
      } else {
        alert(res.data.message || "Check-in failed");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  // ── Map embed URL ────────────────────────────────────────────────────
  const mapSrc = location
    ? `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=16&output=embed`
    : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .ci-frame {
          min-height: 100dvh;
          background: #000;
          display: flex; align-items: flex-start; justify-content: center;
          font-family: 'Plus Jakarta Sans', sans-serif;
          padding: 10px;
        }
        .ci-phone {
          width: 100%; max-width: 420px;
        }
        @media (min-width: 480px) {
          .ci-frame { align-items: center; padding: 20px; }
          .ci-phone { max-width: 440px; }
        }

        /* ── White card ── */
        .ci-card {
          background: #fff;
          border-radius: 30px;
          padding: 52px 14px 14px;
          position: relative;
          overflow: hidden;
        }

        /* ── Header (absolute over card) ── */
        .ci-header {
          position: absolute; top: 12px; left: 14px; right: 14px;
          display: flex; justify-content: space-between; align-items: center;
          z-index: 20;
        }
        .ci-title-pill {
          background: #F2F2F2;
          padding: 6px 15px; border-radius: 20px;
          font-size: 14px; font-weight: 800; color: #111;
        }
        .ci-close {
          width: 35px; height: 35px; border-radius: 50%;
          background: #F2F2F2; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #333; transition: background 0.2s;
        }
        .ci-close:hover { background: #E5E7EB; }

        /* ── Camera box ── */
        .ci-cam-box {
          border-radius: 20px; overflow: hidden;
          background: #111;
          aspect-ratio: 3/4;
          max-height: 52vh;
          position: relative;
        }
        .ci-cam-box video,
        .ci-cam-box img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .ci-cam-box video { transform: scaleX(-1); }
        .ci-cam-flash {
          position: absolute; inset: 0;
          background: #fff; opacity: 0;
          pointer-events: none;
          transition: opacity 0.05s;
        }
        .ci-cam-flash.on { opacity: 0.7; }
        .ci-cam-error {
          position: absolute; inset: 0;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 10px; padding: 20px; text-align: center;
          color: #9CA3AF; font-size: 13px; background: #1a1a1a;
        }

        /* ── Time pill (inside camera) ── */
        .ci-time-pill {
          position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
          background: #fff; border-radius: 25px;
          padding: 6px 14px;
          display: flex; align-items: center; gap: 5px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.25);
          white-space: nowrap;
          font-size: 13px; font-weight: 600; color: #111;
        }

        /* ── Flip button ── */
        .ci-flip-btn {
          position: absolute; top: 12px; right: 12px;
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(0,0,0,0.45);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #fff; transition: background 0.2s;
        }
        .ci-flip-btn:hover { background: rgba(0,0,0,0.65); }

        /* ── Location card ── */
        .ci-loc-card {
          margin-top: 12px;
          background: #F7F7F7;
          border-radius: 20px;
          padding: 10px;
        }
        .ci-loc-header {
          display: flex; align-items: center; gap: 6px;
          margin-bottom: 8px;
          font-size: 13px; font-weight: 600; color: #111;
        }
        .ci-map-box {
          border-radius: 14px; overflow: hidden;
          height: 110px;
          background: #E5E7EB;
        }
        .ci-map-box iframe { width: 100%; height: 100%; border: none; display: block; }
        .ci-map-loading {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          color: #9CA3AF; font-size: 12px; gap: 6px;
        }

        /* ── Coords chip ── */
        .ci-coords {
          margin-top: 6px;
          font-size: 10px; color: #9CA3AF;
          text-align: center; font-weight: 500;
        }

        /* ── Buttons ── */
        .ci-btns { padding-top: 12px; }
        .ci-btn-row { display: flex; gap: 10px; }

        .ci-btn-primary {
          flex: 1;
          background: #000;
          color: #fff;
          border: none; border-radius: 30px;
          padding: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px; font-weight: 700; letter-spacing: 0.4px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: opacity 0.2s, transform 0.15s;
        }
        .ci-btn-primary:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
        .ci-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
        .ci-btn-primary.green {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          box-shadow: 0 4px 14px rgba(5,150,105,0.35);
        }

        .ci-btn-secondary {
          flex: 1;
          background: #F2F2F2;
          color: #333;
          border: 1px solid #E0E0E0; border-radius: 30px;
          padding: 14px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 14px; font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          transition: background 0.2s;
        }
        .ci-btn-secondary:hover { background: #E8E8E8; }

        /* ── Full screen loader overlay ── */
        .ci-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 999;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ci-spinner {
          width: 44px; height: 44px;
          border: 3.5px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.75s linear infinite;
        }

        /* ── Canvas (hidden, used for capture) ── */
        canvas { display: none; }
      `}</style>

      <div className="ci-frame">
        <div className="ci-phone">
          <div className="ci-card">

            {/* Header */}
            <div className="ci-header">
              <div className="ci-title-pill">Check In</div>
              <button className="ci-close" onClick={() => navigate("/dashboard")}>
                <IconClose />
              </button>
            </div>

            {/* Camera / Photo */}
            <div className="ci-cam-box">
              {photo ? (
                <img src={photo} alt="captured" />
              ) : (
                <>
                  <video ref={videoRef} autoPlay playsInline muted />
                  {!camReady && !camError && (
                    <div className="ci-cam-error">
                      <div className="ci-spinner" style={{ borderTopColor: "#7B8DFF" }} />
                      <span>Starting camera…</span>
                    </div>
                  )}
                  {camError && (
                    <div className="ci-cam-error">
                      <IconCamera />
                      <span>{camError}</span>
                    </div>
                  )}
                  {/* Flip button */}
                  {camReady && (
                    <button className="ci-flip-btn" onClick={() => setFacingFront(f => !f)} title="Flip camera">
                      <IconFlip />
                    </button>
                  )}
                </>
              )}

              {/* Shutter flash */}
              <div className={`ci-cam-flash${flash ? " on" : ""}`} />

              {/* Time pill */}
              <div className="ci-time-pill">
                <IconTime />
                {time}
              </div>
            </div>

            {/* Location card */}
            <div className="ci-loc-card">
              <div className="ci-loc-header">
                <IconPin />
                Current Location
              </div>
              <div className="ci-map-box">
                {mapSrc ? (
                  <iframe src={mapSrc} title="location" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                ) : (
                  <div className="ci-map-loading">
                    <div className="ci-spinner" style={{ width: 18, height: 18, borderWidth: 2, borderTopColor: "#7B8DFF" }} />
                    {locError || "Fetching location…"}
                  </div>
                )}
              </div>
              {location && (
                <p className="ci-coords">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="ci-btns">
              {!photo ? (
                <button
                  className="ci-btn-primary"
                  onClick={takePhoto}
                  disabled={!camReady || !!camError}
                >
                  <IconCamera />
                  Take Photo
                </button>
              ) : (
                <div className="ci-btn-row">
                  <button className="ci-btn-secondary" onClick={retake}>
                    <IconRetake />
                    Retake
                  </button>
                  <button
                    className="ci-btn-primary green"
                    style={{ flex: 2 }}
                    onClick={confirmCheckIn}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="ci-spinner" style={{ width: 20, height: 20, borderWidth: 2.5 }} />
                    ) : (
                      <>
                        <IconCheck />
                        Confirm Check In
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} />

      {/* Full-screen loading overlay */}
      {loading && (
        <div className="ci-overlay">
          <div className="ci-spinner" />
        </div>
      )}
    </>
  );
}